/**
 * ResponseOverlay - 回應階段 UI 覆蓋層
 * 顯示回應選項、倒數計時、連鎖堆疊
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useGameStore } from '../../store/gameStore';
import type { ResponseOption, ChainEntry } from '../../types/response';

// ============================================================
// 樣式
// ============================================================

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.9))',
    padding: '20px',
    zIndex: 900,
    animation: 'slideUp 0.3s ease-out',
  },
  container: {
    maxWidth: '1000px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    color: '#ffd700',
    fontSize: '20px',
    fontWeight: 'bold',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  triggerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: 'rgba(255, 100, 100, 0.2)',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid rgba(255, 100, 100, 0.5)',
  },
  triggerIcon: {
    fontSize: '24px',
  },
  triggerText: {
    color: '#fff',
    fontSize: '16px',
  },
  triggerCardName: {
    color: '#ff6b6b',
    fontWeight: 'bold',
  },
  timer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    padding: '8px 16px',
    borderRadius: '20px',
  },
  timerText: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: 'bold',
    minWidth: '40px',
    textAlign: 'center' as const,
  },
  timerBar: {
    width: '100px',
    height: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  timerBarFill: {
    height: '100%',
    backgroundColor: '#ffd700',
    transition: 'width 0.1s linear',
  },
  chainStack: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    maxHeight: '150px',
    overflowY: 'auto' as const,
    padding: '8px',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '8px',
  },
  chainEntry: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '4px',
    borderLeft: '4px solid #4a90d9',
  },
  chainEntryPlayer1: {
    borderLeftColor: '#4a90d9',
  },
  chainEntryPlayer2: {
    borderLeftColor: '#d94a4a',
  },
  chainNumber: {
    backgroundColor: 'rgba(255, 215, 0, 0.3)',
    color: '#ffd700',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  chainCardName: {
    color: '#fff',
    fontWeight: 'bold',
  },
  chainEffect: {
    color: '#aaa',
    fontSize: '13px',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  responsesContainer: {
    display: 'flex',
    gap: '12px',
    overflowX: 'auto' as const,
    padding: '8px 0',
  },
  responseCard: {
    flexShrink: 0,
    width: '180px',
    backgroundColor: 'rgba(50, 50, 80, 0.9)',
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    border: '2px solid transparent',
  },
  responseCardHover: {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
    borderColor: '#ffd700',
  },
  responseCardImage: {
    width: '100%',
    height: '120px',
    objectFit: 'cover' as const,
    backgroundColor: '#333',
  },
  responseCardInfo: {
    padding: '8px',
  },
  responseCardName: {
    color: '#fff',
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '4px',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  responseCardType: {
    color: '#888',
    fontSize: '12px',
    marginBottom: '4px',
  },
  responseCardEffect: {
    color: '#88ccff',
    fontSize: '11px',
    lineHeight: 1.4,
    maxHeight: '44px',
    overflow: 'hidden',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  passButton: {
    padding: '12px 32px',
    fontSize: '16px',
    backgroundColor: 'rgba(100, 100, 100, 0.8)',
    color: '#fff',
    border: '2px solid #666',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontWeight: 'bold',
  },
  passButtonHover: {
    backgroundColor: 'rgba(150, 150, 150, 0.8)',
    borderColor: '#888',
  },
  waitingMessage: {
    textAlign: 'center' as const,
    color: '#aaa',
    fontSize: '16px',
    padding: '20px',
  },
  waitingDots: {
    display: 'inline-block',
    animation: 'dots 1.5s steps(4, end) infinite',
  },
};

// CSS 動畫（會在 useEffect 中注入）
const keyframesCSS = `
  @keyframes slideUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes dots {
    0%, 20% { content: '.'; }
    40% { content: '..'; }
    60% { content: '...'; }
    80%, 100% { content: ''; }
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

// ============================================================
// Props
// ============================================================

export interface ResponseOverlayProps {
  /** 是否顯示 */
  isOpen: boolean;
  /** 觸發的卡牌名稱 */
  triggerCardName: string | null;
  /** 觸發者玩家 ID */
  triggerPlayerId: string | null;
  /** 當前擁有優先權的玩家 ID */
  currentPriority: string | null;
  /** 本地玩家 ID */
  localPlayerId: string;
  /** 可用的回應選項 */
  availableResponses: ResponseOption[];
  /** 連鎖堆疊 */
  chainEntries: ChainEntry[];
  /** 剩餘時間（秒），null 表示不顯示計時器 */
  timeRemaining: number | null;
  /** 最大時間（秒） */
  maxTime: number;
  /** 是否在等待 AI */
  waitingForAI: boolean;
  /** 選擇回應 */
  onRespond: (response: ResponseOption) => void;
  /** 放棄回應 */
  onPass: () => void;
}

// ============================================================
// 子組件
// ============================================================

interface ResponseCardProps {
  response: ResponseOption;
  imagePath?: string;
  onClick: () => void;
}

function ResponseCard({ response, imagePath, onClick }: ResponseCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        ...styles.responseCard,
        ...(isHovered ? styles.responseCardHover : {}),
      }}
      onMouseEnter={() => { setIsHovered(true); }}
      onMouseLeave={() => { setIsHovered(false); }}
      onClick={onClick}
    >
      <img
        src={imagePath || '/placeholder-card.png'}
        alt={response.cardName}
        style={styles.responseCardImage}
        onError={(e) => {
          (e.target as HTMLImageElement).src = '/placeholder-card.png';
        }}
      />
      <div style={styles.responseCardInfo}>
        <div style={styles.responseCardName}>{response.cardName}</div>
        <div style={styles.responseCardType}>
          {response.type === 'spell' ? '魔法卡' : 
           response.type === 'unit_ability' ? '單位效果' : '手牌觸發'}
        </div>
        <div style={styles.responseCardEffect}>
          {response.effectText}
        </div>
      </div>
    </div>
  );
}

interface ChainStackProps {
  entries: ChainEntry[];
  player1Id: string;
}

function ChainStack({ entries, player1Id }: ChainStackProps) {
  if (entries.length === 0) return null;

  return (
    <div style={styles.chainStack}>
      {entries.map((entry, index) => (
        <div
          key={entry.id}
          style={{
            ...styles.chainEntry,
            ...(entry.playerId === player1Id
              ? styles.chainEntryPlayer1
              : styles.chainEntryPlayer2),
          }}
        >
          <span style={styles.chainNumber}>{entries.length - index}</span>
          <span style={styles.chainCardName}>{entry.sourceCardName}</span>
          <span style={styles.chainEffect}>{entry.effectText}</span>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// 主組件
// ============================================================

export function ResponseOverlay({
  isOpen,
  triggerCardName,
  triggerPlayerId,
  currentPriority,
  localPlayerId,
  availableResponses,
  chainEntries,
  timeRemaining,
  maxTime,
  waitingForAI,
  onRespond,
  onPass,
}: ResponseOverlayProps) {
  const [passHovered, setPassHovered] = useState(false);
  const cardDefinitions = useGameStore(state => state.cardDefinitions);
  const gameState = useGameStore(state => state.gameState);

  // 注入 CSS 動畫
  useEffect(() => {
    const styleId = 'response-overlay-keyframes';
    if (!document.getElementById(styleId)) {
      const styleEl = document.createElement('style');
      styleEl.id = styleId;
      styleEl.textContent = keyframesCSS;
      document.head.appendChild(styleEl);
    }
  }, []);

  // 取得卡牌圖片路徑
  const getCardImagePath = useCallback((response: ResponseOption): string | undefined => {
    if (!response.cardCode) return undefined;
    const def = cardDefinitions.get(response.cardCode);
    return def?.imagePath;
  }, [cardDefinitions]);

  // 是否輪到本地玩家回應
  const isMyTurn = currentPriority === localPlayerId;

  // 計算計時器百分比
  const timerPercent = useMemo(() => {
    if (timeRemaining === null || maxTime === 0) return 100;
    return (timeRemaining / maxTime) * 100;
  }, [timeRemaining, maxTime]);

  // 取得 player1 ID
  const player1Id = gameState?.player1.id ?? '';

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <h3 style={styles.title}>
            <span>⚡</span>
            回應階段
          </h3>

          {/* Timer */}
          {timeRemaining !== null && (
            <div style={styles.timer}>
              <span style={styles.timerText}>
                {Math.ceil(timeRemaining)}s
              </span>
              <div style={styles.timerBar}>
                <div
                  style={{
                    ...styles.timerBarFill,
                    width: `${String(timerPercent)}%`,
                    backgroundColor: timerPercent < 30 ? '#ff4444' : '#ffd700',
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Trigger Info */}
        {triggerCardName && (
          <div style={styles.triggerInfo}>
            <span style={styles.triggerIcon}>🎯</span>
            <span style={styles.triggerText}>
              {triggerPlayerId === localPlayerId ? '你' : '對手'}發動了{' '}
              <span style={styles.triggerCardName}>{triggerCardName}</span>
            </span>
          </div>
        )}

        {/* Chain Stack */}
        {chainEntries.length > 1 && (
          <ChainStack entries={chainEntries} player1Id={player1Id} />
        )}

        {/* Waiting for AI */}
        {waitingForAI && !isMyTurn && (
          <div style={styles.waitingMessage}>
            對手正在思考
            <span style={styles.waitingDots}>...</span>
          </div>
        )}

        {/* Response Options - 只在輪到玩家時顯示 */}
        {isMyTurn && (
          <>
            {availableResponses.length > 0 ? (
              <div style={styles.responsesContainer}>
                {availableResponses.map(response => (
                  <ResponseCard
                    key={response.cardId}
                    response={response}
                    imagePath={getCardImagePath(response)}
                    onClick={() => { onRespond(response); }}
                  />
                ))}
              </div>
            ) : (
              <div style={styles.waitingMessage}>
                沒有可用的回應牌
              </div>
            )}

            {/* Actions */}
            <div style={styles.actions}>
              <button
                style={{
                  ...styles.passButton,
                  ...(passHovered ? styles.passButtonHover : {}),
                }}
                onMouseEnter={() => { setPassHovered(true); }}
                onMouseLeave={() => { setPassHovered(false); }}
                onClick={onPass}
              >
                放棄回應
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ResponseOverlay;
